import { useEffect, useMemo, useState } from 'react'
import { TableConfigService } from './tableConfigService'

export const useTableConfig = (idTable: string, columns: any[]) => {
	const [initialColumnsIdsOrder, setInitialColumnsIdsOrder] = useState<
		string[]
	>([])
	const [appliedColumnsFilters, setAppliedColumnsFilters] =
		useState<Record<string, { value: any; orderBy: 'ASC' | 'DESC' }>>()
	const [isTableConfigApiFullfilled, setIsTableConfigApiFullfilled] =
		useState(false)

	useEffect(() => {
		return () => {
			setIsTableConfigApiFullfilled(false)
			setAppliedColumnsFilters({})
		}
	}, [])
	
	useEffect(() => {
		setIsTableConfigApiFullfilled(false)
		const fetchConfig = async () => {
			setInitialColumnsIdsOrder([])
			const response = await TableConfigService.GetTableConfig(idTable)
			/**
			 * If the user who makes the request does not yet have a configuration for tableX
			 * then the service will return the rearrangeOrder field as an empty list ([]).
			 */
			if (response.data?.rearrangeOrder?.length > 0) {
				setInitialColumnsIdsOrder(response.data.rearrangeOrder)
			}
			setAppliedColumnsFilters(response.data?.columns)
			setIsTableConfigApiFullfilled(true)
		}
		fetchConfig()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idTable])

	/**
	 * At each rearrange operation performed on a table,
	 * the frontend will have to send the new configuration to the backend via the PUT table_config service.
	 * The body will contain a JSON with only the rearrangeOrder field.
	 */
	const setOrderColumns = async (rearrangeOrder: string[]) => {
		await TableConfigService.PutTableConfig(idTable, {
			rearrangeOrder,
			columns: appliedColumnsFilters,
		})
		setInitialColumnsIdsOrder(columns)
	}
	/**
	 * At each rearrange operation performed on a table,
	 * the frontend will have to send the new configuration to the backend via the PUT table_config service.
	 * The body will contain a JSON with only the rearrangeOrder field.
	 */
	const setFiltersColumns = async (
		columns: Record<string, { value: any; orderBy: 'ASC' | 'DESC' }>
	) => {
		await TableConfigService.PutTableConfig(idTable, {
			columns,
			rearrangeOrder: initialColumnsIdsOrder,
		})
		setAppliedColumnsFilters(columns)
	}

	const reorderedColumns = useMemo<any[]>(() => {
		if (initialColumnsIdsOrder.length > 0) {
			const cloneColumns = columns.map((column) => column)
			return cloneColumns.sort((a, b) => {
				return (
					initialColumnsIdsOrder.indexOf(a.field) -
					initialColumnsIdsOrder.indexOf(b.field)
				)
			})
		} else {
			return columns
		}
	}, [initialColumnsIdsOrder, columns])

	return {
		isTableConfigApiFullfilled,
		appliedColumnsFilters,
		reorderedColumns,
		setOrderColumns,
		setFiltersColumns,
	}
}
